import { useMedicationReminders } from '@/hooks/use-medication-reminders';
import { GlassCard } from '@/components/ui/glass-card';
import { cn } from '@/lib/utils';
import {
  Check,
  Clock,
  AlertCircle,
  Flame,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { format } from 'date-fns';

const statusConfig = {
  pending: {
    color: 'text-gray-500 bg-gray-100 dark:bg-gray-700',
    icon: Clock,
  },
  taken: {
    color: 'text-green-600 bg-green-100 dark:bg-green-900/30',
    icon: Check,
  },
  missed: {
    color: 'text-red-600 bg-red-100 dark:bg-red-900/30',
    icon: AlertCircle,
  },
  overdue: {
    color: 'text-red-600 bg-red-100 dark:bg-red-900/30',
    icon: AlertCircle,
  },
  skipped: {
    color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
    icon: Clock,
  },
  snoozed: {
    color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
    icon: Clock,
  },
};

const getAdherenceColor = (rate: number) => {
  if (rate >= 90) return 'text-green-600 dark:text-green-400';
  if (rate >= 70) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
};

const getAdherenceRingColor = (rate: number) => {
  if (rate >= 90) return '#10b981'; // green-500
  if (rate >= 70) return '#f59e0b'; // amber-500
  return '#ef4444'; // red-500
};

export default function MedicationAdherenceWidget() {
  const {
    reminders,
    adherence,
    logMedication,
    overdueCount,
    isLoading
  } = useMedicationReminders();

  const handleTakeMedication = (reminder: typeof reminders[0]) => {
    logMedication({
      medicationId: reminder.medicationId,
      action: 'taken',
      scheduledTime: reminder.scheduledTime,
      actualTime: new Date().toISOString(),
    });
  };

  if (isLoading) {
    return (
      <GlassCard className="w-full">
        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="w-full">
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Today's Medications
          </h2>
          {overdueCount > 0 && (
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-bold">
              <AlertCircle className="h-3 w-3" />
              {overdueCount} Overdue
            </div>
          )}
        </div>

        {/* Medication Reminders */}
        {reminders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
              <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No medications scheduled for today
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {reminders.map((reminder) => {
              const statusInfo = statusConfig[reminder.status];
              const StatusIcon = statusInfo.icon;
              const isActionable = reminder.status === 'pending' || reminder.isOverdue;

              return (
                <div
                  key={reminder.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border transition-all",
                    reminder.isOverdue
                      ? "border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10"
                      : "border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50"
                  )}
                >
                  {/* Status Icon */}
                  <div className={cn("p-2 rounded-full", statusInfo.color)}>
                    <StatusIcon className="h-4 w-4" />
                  </div>

                  {/* Medication Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                        {reminder.medicationName}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {reminder.dosage}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {format(new Date(reminder.scheduledTime), 'h:mm a')}
                      </span>
                      {reminder.isOverdue && (
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">
                          Overdue
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Button */}
                  {isActionable ? (
                    <button
                      onClick={() => handleTakeMedication(reminder)}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors flex-shrink-0"
                    >
                      Take
                    </button>
                  ) : reminder.status === 'taken' ? (
                    <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium flex-shrink-0">
                      <Check className="h-3 w-3" />
                      Taken
                    </div>
                  ) : reminder.status === 'snoozed' ? (
                    <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium flex-shrink-0">
                      <Clock className="h-3 w-3" />
                      Snoozed
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {/* Adherence Section */}
        {adherence && adherence.length > 0 && (
          <>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                Adherence Tracking
              </h3>
              <div className="space-y-4">
                {adherence.map((med, idx) => {
                  const TrendIcon = med.trend === 'improving' ? TrendingUp : med.trend === 'declining' ? TrendingDown : TrendingUp;
                  const trendColor = med.trend === 'improving' ? 'text-green-600 dark:text-green-400' : med.trend === 'declining' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400';

                  return (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {med.medicationName}
                          </span>
                          {med.streakDays > 0 && (
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                              <Flame className="h-3 w-3" />
                              <span className="text-xs font-medium">{med.streakDays}-day streak</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={cn("text-sm font-bold", getAdherenceColor(med.adherenceRate))}>
                            {med.adherenceRate}%
                          </span>
                          <TrendIcon className={cn("h-4 w-4", trendColor)} />
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                          style={{
                            width: `${med.adherenceRate}%`,
                            backgroundColor: getAdherenceRingColor(med.adherenceRate),
                          }}
                        />
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                        <span>{med.totalDoses - med.missedDoses} taken</span>
                        {med.missedDoses > 0 && (
                          <span className="text-red-600 dark:text-red-400">
                            {med.missedDoses} missed
                          </span>
                        )}
                        <span className="text-gray-500 dark:text-gray-500">
                          • {med.period}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </GlassCard>
  );
}
